package com.swetdot.swetgram.data.realtime

import com.swetdot.swetgram.data.api.ApiConfig
import com.swetdot.swetgram.protocol.dto.RealtimeEvent
import com.swetdot.swetgram.protocol.dto.RealtimeTokenResponse
import io.ktor.client.HttpClient
import io.ktor.client.plugins.websocket.webSocketSession
import io.ktor.websocket.Frame
import io.ktor.websocket.WebSocketSession
import io.ktor.websocket.close
import io.ktor.websocket.readText
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlin.time.Instant

/**
 * What a consumer of the socket must react to.
 *
 * [Resynchronize] exists because DECISIONS §11 makes realtime explicitly unreliable: "WebSocket
 * delivery is not treated as durable truth ... after reconnect, the client reconciles messages,
 * edits, and deletion tombstones through REST." Anything that happened while the socket was down
 * arrives as nothing at all, so a reconnect has to mean "refetch", not "carry on". Modelling that
 * as a signal rather than a comment means a screen cannot forget it.
 */
sealed interface RealtimeSignal {
    /** Connected or reconnected. Refetch over REST -- events may have been missed entirely. */
    data object Resynchronize : RealtimeSignal

    data class Event(val event: RealtimeEvent) : RealtimeSignal

    /**
     * Somebody started or stopped typing. Carries the whole fact, so a consumer renders it
     * directly and MUST NOT answer it with a REST read.
     *
     * A case of its own rather than another [Event] precisely so that consumers of [Event] --
     * every one of which refetches -- never see it. Note there is no reconciliation available
     * even if one wanted it: typing is stored nowhere, so a dropped frame is recovered by the
     * receiver's expiry timer rather than by asking the server.
     */
    data class Typing(
        val conversationId: String,
        val userId: String,
        val isTyping: Boolean,
    ) : RealtimeSignal

    /**
     * [userId] has read this conversation up to and including [readUpTo].
     *
     * A case of its own, and NOT an [Event], for the reason the whole feature exists: every consumer
     * of [Event] answers it with `repository.refresh`, and reloading a conversation because a tick
     * turned is exactly the cost this frame was added to avoid. It carries the whole fact so the
     * receiver can fold it into the timeline instead.
     *
     * [readUpTo] is a WATERMARK, not an event time -- it is the `created_at` of the message the
     * reader has reached, so every own message at or before it is read. Two rules follow, and both
     * live in `ChatViewModel` because they are about a conversation rather than a socket: the mark
     * never moves backwards, and it is not applied at all where the reader is not the only other
     * participant.
     *
     * Unlike [Typing] there IS somewhere to reconcile from -- `Message.status` over REST -- so a
     * dropped frame costs a late tick rather than a missing fact.
     */
    data class Read(
        val conversationId: String,
        val userId: String,
        val readUpTo: Instant,
    ) : RealtimeSignal
}

/** Where connection credentials come from. Separated so tests can supply their own. */
fun interface RealtimeTokenSource {
    suspend fun issue(): RealtimeTokenResponse
}

/**
 * The Centrifugo channel one conversation's events travel on.
 *
 * The server's spelling of this lives in `server/realtime/Centrifugo.kt`'s `Channels`, which
 * `:shared` cannot see -- the two modules share `:protocol` and nothing else, and moving a server
 * type into the contract module to share one string would be the wrong trade. It is only ever
 * compared against the `channels` list the token endpoint returns, never sent, so the worst a
 * drift between the two ends can cost is one wasted reconnect per conversation opened.
 */
internal fun conversationChannel(conversationId: String): String = "conversation:$conversationId"

/**
 * Whether [RealtimeClient.ensureSubscribed] should drop the socket for [channel].
 *
 * Pulled out as a pure function because every one of its three answers is a decision the running
 * app makes invisibly -- dropping a healthy connection, or failing to -- and none of them can be
 * observed from outside without a live Centrifugo.
 *
 * [allowed] empty means no token has been issued yet: there is nothing stale to fix, because the
 * first one is read from the database when it is signed and will contain this conversation.
 * [alreadyTried] is what stops a chat the server will not grant -- one this user was removed from,
 * one deleted underneath them -- from dropping a working socket every time its screen is opened.
 */
internal fun shouldReconnectFor(
    channel: String,
    allowed: Set<String>,
    alreadyTried: Set<String>,
): Boolean = allowed.isNotEmpty() && channel !in allowed && channel !in alreadyTried

/**
 * Centrifugo client, speaking its JSON protocol directly over Ktor's WebSocket.
 *
 * Hand-rolled on purpose: the official Centrifugo SDKs are Android/JVM or Swift, and `:shared`
 * has to compile for both. The part of the protocol this needs is small -- connect, pong, push.
 *
 * The socket carries notifications, never content: every [RealtimeEvent] is ids only, and the
 * screens answer one with a REST read.
 */
class RealtimeClient(
    private val http: HttpClient,
    private val tokenSource: RealtimeTokenSource,
    private val url: String = ApiConfig.REALTIME_URL,
) {
    companion object {
        /** Ladder: 1s, 2s, 4s ... capped, so a server that is down is not hammered. */
        private const val BACKOFF_MIN_MS = 1_000L
        private const val BACKOFF_MAX_MS = 30_000L
    }

    enum class State { Disconnected, Connecting, Connected }

    private val _state = MutableStateFlow(State.Disconnected)
    val state: StateFlow<State> = _state.asStateFlow()

    /**
     * Replay 0, and the buffer drops oldest on overflow: a signal nothing was listening for is
     * not worth keeping, and a slow collector must never stall the socket's read loop.
     */
    private val _signals = MutableSharedFlow<RealtimeSignal>(
        replay = 0,
        extraBufferCapacity = 64,
        onBufferOverflow = BufferOverflow.DROP_OLDEST,
    )
    val signals: SharedFlow<RealtimeSignal> = _signals.asSharedFlow()

    private var loop: Job? = null

    /** Cancelling this alone drops the current socket; the outer loop then reconnects. */
    private var session: Job? = null

    /**
     * The channels the CURRENT connection's token actually allows, straight from the issuing
     * response. Empty until one has been issued.
     *
     * A `StateFlow` rather than a plain field because it is written on the socket's coroutine and
     * read from whichever one asks -- the main thread, in practice. A plain `var` shared across
     * threads is exactly the kind of thing that works on the JVM and is undefined on Native.
     */
    private val channels = MutableStateFlow<Set<String>>(emptySet())

    /**
     * Conversations [ensureSubscribed] has already reconnected for.
     *
     * Guards against a reconnect loop: if the server does not put the channel in the next token
     * either -- the caller is not a member, the chat was deleted server-side -- retrying on every
     * screen open would drop a working socket over and over.
     */
    private val reconnectedFor = MutableStateFlow<Set<String>>(emptySet())

    fun start(scope: CoroutineScope) {
        if (loop?.isActive == true) return
        loop = scope.launch { runLoop() }
    }

    fun stop() {
        loop?.cancel()
        loop = null
        _state.value = State.Disconnected
    }

    /**
     * Drops the socket so the loop reconnects with a freshly issued token.
     *
     * Needed because the allowed channels are baked into the token when it is signed: a
     * conversation created afterwards is not in the claim, and Centrifugo enforces the claim
     * rather than the client's request. Without this a brand-new chat stays silent until the
     * token expires.
     */
    fun refreshSubscriptions() {
        session?.cancel()
    }

    /**
     * Makes sure the socket is actually carrying one conversation's channel, reconnecting if it
     * is not. Cheap and a no-op in the ordinary case.
     *
     * WHY A SCREEN HAS TO ASK. The channel list is a signed claim fixed when the token was
     * issued (`RealtimeRoutes` reads the caller's memberships at that moment), so a conversation
     * that did not exist yet -- one just created, or one somebody else opened with this user --
     * is not in it. Centrifugo enforces the claim rather than the client's request, so the socket
     * stays up, looks healthy, and silently delivers NOTHING for that chat until the token
     * expires. For messages that is survivable, because the screen also reads REST. For typing it
     * is total: it is published to the conversation channel and stored nowhere, so a frame that
     * does not arrive is a frame that never existed.
     *
     * Called when a conversation screen opens, which is both the moment it starts to matter and
     * the only moment the client knows which channel it needs.
     */
    fun ensureSubscribed(conversationId: String) {
        val channel = conversationChannel(conversationId)
        if (!shouldReconnectFor(channel, channels.value, reconnectedFor.value)) return
        reconnectedFor.value = reconnectedFor.value + channel
        refreshSubscriptions()
    }

    private suspend fun CoroutineScope.runLoop() {
        var failures = 0
        while (isActive) {
            try {
                _state.value = State.Connecting
                connectOnce()
                // A clean close still means frames could have been missed, so reconnecting
                // resynchronises exactly like recovering from an error does.
                failures = 0
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                // Deliberately swallowed. A socket dropping is ordinary -- a tunnel dies, a phone
                // changes network -- and REST stays the path that reports real errors to a user.
                failures++
            } finally {
                _state.value = State.Disconnected
            }
            delay(backoffFor(failures))
        }
    }

    /** Exponential with a cap. Zero failures (a clean disconnect) retries at the minimum. */
    private fun backoffFor(failures: Int): Long {
        var delayMs = BACKOFF_MIN_MS
        repeat(minOf(maxOf(failures - 1, 0), 10)) { delayMs *= 2 }
        return minOf(delayMs, BACKOFF_MAX_MS)
    }

    private suspend fun connectOnce() {
        // Issued per connection rather than cached: the token both authenticates and carries the
        // channel list, so a stale one subscribes to the wrong set of conversations.
        val credentials = tokenSource.issue()
        // Recorded BEFORE the socket opens, so `ensureSubscribed` never judges a new connection
        // by the previous one's claim.
        channels.value = credentials.channels.toSet()

        val socket = http.webSocketSession(urlString = url)
        session = socket.coroutineContext[Job]
        try {
            socket.send(Frame.Text(CentrifugoProtocol.connectCommand(credentials.token)))

            for (frame in socket.incoming) {
                if (frame !is Frame.Text) continue
                // Centrifugo may pack several commands into one frame, newline separated.
                for (line in frame.readText().lineSequence()) {
                    if (line.isNotBlank()) handle(line, socket)
                }
            }
        } finally {
            session = null
            runCatching { socket.close() }
        }
    }

    private suspend fun handle(line: String, socket: WebSocketSession) {
        when (val frame = CentrifugoProtocol.parse(line)) {
            is CentrifugoFrame.Ping -> socket.send(Frame.Text(CentrifugoProtocol.PONG))

            is CentrifugoFrame.ConnectAccepted -> {
                _state.value = State.Connected
                _signals.emit(RealtimeSignal.Resynchronize)
            }

            // Thrown rather than logged: the loop's reconnect issues a NEW token, which is the
            // only thing that can fix a rejection. Staying on a socket that will never deliver
            // anything is the failure mode worth avoiding.
            is CentrifugoFrame.ConnectRejected -> throw RealtimeRejected(frame.reply)

            is CentrifugoFrame.Push -> _signals.emit(RealtimeSignal.Event(frame.event))

            is CentrifugoFrame.Typing -> _signals.emit(
                RealtimeSignal.Typing(
                    conversationId = frame.event.conversationId,
                    userId = frame.event.userId,
                    isTyping = frame.event.isTyping,
                ),
            )

            is CentrifugoFrame.ReadReceipt -> _signals.emit(
                RealtimeSignal.Read(
                    conversationId = frame.conversationId,
                    userId = frame.userId,
                    readUpTo = frame.readUpTo,
                ),
            )

            is CentrifugoFrame.Ignored -> Unit
        }
    }
}

/** Centrifugo refused the connection -- almost always an expired or mis-signed token. */
class RealtimeRejected(reply: String) : Exception("Centrifugo rejected the connection: $reply")
